/**
 * Wire shape for the subscribe handshake's ``ready`` ack, as it crosses the
 * Socket.IO boundary from the message-gateway.
 *
 * The backend emits ``STATUS_UPDATE { status: 'ready', welcome, inflightRun }``
 * once the subscribe handshake completes. These two fields let the FE resolve
 * the composer's load gate atomically (OR-6139 AC 2):
 *
 *  - ``welcome`` — whether a greeting will stream (or is still streaming). When
 *    false the FE clears ``initializing`` on the ack instead of waiting for a
 *    welcome-complete event that will never come (gen_3 suppresses the welcome).
 *  - ``inflightRun`` — the run still processing on the server, or ``null``. When
 *    present the FE flips straight to the in-flight (Stop) state in the same
 *    reducer that opens the submit gate, so no frame of Send shows first.
 *
 * Fields are optional for back-compat with gateways that predate this change:
 * a missing ``welcome`` defaults to ``false`` (treat as "no greeting coming",
 * matching the prior behaviour where ``ready`` alone enabled the composer), and
 * a missing ``inflightRun`` defaults to ``null``.
 */
import { z } from 'zod';

export const ReadyAckSchema = z.object({
  welcome: z.boolean().default(false),
  inflightRun: z
    .object({ runId: z.string().min(1) })
    .nullish()
    .transform((v) => v ?? null),
  // ``interruptedRun`` — an orphaned run whose pod died (lease lapsed) with no
  // terminal event. Carried on ``ready`` (always emitted last) so the FE applies
  // the Retry-card state atomically AFTER the snapshot's ``hydrated`` reset,
  // which would otherwise null it on a multi-subscribe reconnect. Mutually
  // exclusive with ``inflightRun`` (a run is either still alive or dead).
  interruptedRun: z
    .object({ runId: z.string().min(1), retryable: z.boolean().default(true) })
    .nullish()
    .transform((v) => v ?? null),
});

export type ReadyAck = z.infer<typeof ReadyAckSchema>;
